/** #1030: the 16 published NFL player families. Classification only: this is
 * not player identity, cross-venue pairing, a payout comparator, feed freshness
 * or permission to trade. Unknown/absent types never become full-game props.
 * Pure and platform-neutral; discovery and the shared host validate separately. */
const family = <S extends string, P extends string | null, K extends 'threshold' | 'binary'>(
  stat: S, presentationStat: P, kind: K,
) => Object.freeze({ stat, presentationStat, kind, period: 'full' as const });

export const US_NFL_PLAYER_FAMILIES = Object.freeze({
  football_player_receptions: family('rec', 'rec', 'threshold'),
  football_player_passing_yards: family('pass-yds', 'pass-yds', 'threshold'),
  football_player_passing_touchdowns: family('pass-td', 'pass-td', 'threshold'),
  football_player_receiving_yards: family('rec-yds', 'rec-yds', 'threshold'),
  football_player_rushing_yards: family('rush-yds', 'rush-yds', 'threshold'),
  football_player_rushing_attempts: family('rush-att', 'rush-att', 'threshold'),
  football_player_scrimmage_yards: family('rush-rec-yds', 'rush-rec-yds', 'threshold'),
  football_player_longest_reception: family('long-rec', 'long-rec', 'threshold'),
  football_player_passing_attempts: family('pass-att', 'pass-att', 'threshold'),
  football_player_passing_completions: family('pass-comp', 'pass-comp', 'threshold'),
  football_player_touchdowns: family('td', 'td', 'threshold'),
  football_player_interceptions_thrown: family('pass-int', 'pass-int', 'threshold'),
  football_player_first_touchdown: family('first-td', 'first-td', 'binary'),
  // These need their own presentation; never alias them to a nearby K stat.
  football_player_fantasy_points_ppr: family('fantasy-ppr', null, 'threshold'),
  football_player_team_first_touchdown: family('team-first-td', null, 'binary'),
  football_player_most_passing_yards: family('most-passing-yards', null, 'binary'),
});

export type UsNflPlayerFamilyType = keyof typeof US_NFL_PLAYER_FAMILIES;
export type UsNflPlayerFamily = typeof US_NFL_PLAYER_FAMILIES[UsNflPlayerFamilyType];

/** An explicit known venue type supplies full-game scope even when Core's
 * period is absent. presentationStat is only a label/host reuse opportunity,
 * never proof that native strikes or YES/NO outcomes match another venue. */
export function usNflPlayerFamily(value: unknown): UsNflPlayerFamily | null {
  return typeof value === 'string' && Object.prototype.hasOwnProperty.call(US_NFL_PLAYER_FAMILIES, value)
    ? US_NFL_PLAYER_FAMILIES[value as UsNflPlayerFamilyType] : null;
}

export interface UsNflPlayerMetadata {
  player: string;
  playerTeam: string | null;
  stat: UsNflPlayerFamily['stat'];
  kind: UsNflPlayerFamily['kind'];
  period: 'full';
}

/** Source display identity, never a cross-venue player key or a payout rule. */
export function usNflPlayerMetadata(type: unknown, value: unknown, gameKey: string): UsNflPlayerMetadata | null {
  const f = usNflPlayerFamily(type);
  if (!f || !gameKey.startsWith('nfl|') || !value || typeof value !== 'object' || Array.isArray(value)) return null;
  const r = value as Record<string, unknown>;
  if (typeof r.player !== 'string' || !r.player.trim() || r.player.length > 256
    || /[\u0000-\u001f\u007f]/.test(r.player) || r.stat !== f.stat
    || (r.kind !== undefined && r.kind !== f.kind) || (r.period != null && r.period !== f.period)
    || (r.playerTeam != null && (typeof r.playerTeam !== 'string' || !gameKey.split('|').slice(3, 5).includes(r.playerTeam)))) return null;
  // Selected-team first TD cannot lose the selected team.
  if (f.stat === 'team-first-td' && r.playerTeam == null) return null;
  return { player: r.player.trim(), playerTeam: r.playerTeam == null ? null : String(r.playerTeam),
    stat: f.stat, kind: f.kind, period: f.period };
}
